// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { merge, Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map, startWith, switchMap } from 'rxjs/operators';
import { Response } from '@fc-core/services/news.service';
import { LocationInterface } from '../models/location-interface';
import { AppIconsType, iconsList } from '@fc-shared/ui/icon/icons';
import { setCssVariableValue } from '@fc-shared/utils/styles.utils';

export interface LocationType {
  color?: string;
  icon?: AppIconsType;
  id: number;
  name: string;
}

export const locationTypesIcons: AppIconsType[] = [
  'icon-parking-bold',
  'icon-restaurant-bold',
  'icon-home-bold',
  'icon-gas-station-bold',
  'icon-business-center-bold',
  'icon-swap-calls',
  'icon-warning-bold',
  'icon-drop',
  'icon-working-yard-bold',
  'icon-landscape-bold',
  'icon-heart-bold',
  'icon-shield-tick-bold',
  'icon-flag-bold',
  'icon-flash-bold',
  'icon-flame-bold',
  'icon-minus-circle-bold',
  'icon-hotel-bold',
  'icon-pick-up',
];

export function generateLocationTypesCssIconsClasses() {
  locationTypesIcons.forEach((iconName) => {
    setCssVariableValue('--' + iconName, iconsList[iconName]);
  });
}

generateLocationTypesCssIconsClasses();

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  constructor(private http: HttpClient) {}

  saveLocation(location: LocationInterface): Observable<LocationInterface> {
    const payload = Object.assign({}, location);
    payload.photo = null;
    return this.http.post<LocationInterface>(
      environment.apiUrl + 'api/locations/',
      payload,
    );
  }

  addPhoto(photo: File, id: number): Observable<LocationInterface> {
    const formData = new FormData();
    formData.append('photo', photo);
    return this.http.patch<LocationInterface>(
      environment.apiUrl + `api/locations/${id}/`,
      formData,
    );
  }

  addLocationType(
    locationType: Omit<LocationType, 'id'>,
  ): Observable<LocationType> {
    return this.http.post<LocationType>(
      environment.apiUrl + 'api/location-types/',
      locationType,
    );
  }

  updateLocationType(locationType: LocationType): Observable<LocationType> {
    return this.http.put<LocationType>(
      `${environment.apiUrl}api/location-types/${locationType.id}/`,
      locationType,
    );
  }

  deleteLocationType(locationType: LocationType): Observable<void> {
    return this.http.delete<void>(
      environment.apiUrl + `api/location-types/${locationType.id}/`,
    );
  }

  updateLocation(location: LocationInterface): Observable<LocationInterface> {
    const payload = Object.assign({}, location);
    delete payload.photo;
    return this.http.patch<LocationInterface>(
      environment.apiUrl + `api/locations/${location.id}/`,
      payload,
    );
  }

  makeRequests(count: number, params: HttpParams): Observable<any> {
    return this.http.get<Response>(
      environment.apiUrl + `api/locations/` + `?limit=10&offset=${count}`,
      {
        params: params,
      },
    );
  }

  getCurrentLocationInfo(locationId: number): Observable<LocationInterface> {
    return this.http.get(environment.apiUrl + `api/locations/${locationId}/`);
  }

  loadAllLocationsAsync(
    bbox?: number[],
    fields?: string[],
  ): Observable<{
    locationList: LocationInterface[];
    count: number;
    end: boolean;
  }> {
    let queryParams: HttpParams = new HttpParams();
    if (bbox) queryParams = queryParams.set('in_bbox', bbox.toString());
    if (fields) {
      queryParams = queryParams.set('fields', fields.join());
    }
    return this.http
      .get<Response>(environment.apiUrl + `api/locations/` + '?limit=10', {
        params: queryParams,
      })
      .pipe(
        switchMap((data: Response) => {
          const requests: Observable<Response>[] = [];
          if (data.count > 10) {
            for (let i = 10; i <= data.count; i = i + 10) {
              requests.push(this.makeRequests(i, queryParams));
            }
            return merge(...requests).pipe(
              startWith(data),
              map((resp: Response) => {
                return {
                  locationList: resp.results,
                  count: data.count,
                  end: resp.results.length === data.count,
                };
              }),
            );
          }
          return of({
            locationList: data.results,
            count: data.count,
            end: true,
          });
        }),
      );
  }

  getLocationById(id: string): Observable<LocationInterface> {
    return this.http.get<LocationInterface>(
      environment.apiUrl + `api/locations/${id}/`,
    );
  }

  getLocationTypes(): Observable<LocationType[]> {
    return this.http
      .get<LocationType[]>(environment.apiUrl + 'api/location-types/')
      .pipe(
        map((res: any) =>
          res.results.sort((a: LocationType, b: LocationType) =>
            a.name.localeCompare(b.name),
          ),
        ),
      );
  }

  getLocationTypeById(id: number): Observable<LocationType> {
    return this.http.get<LocationType>(
      environment.apiUrl + `api/location-types/${id}/`,
    );
  }

  deleteLocation(location: LocationInterface): Observable<void> {
    return this.http.delete<void>(
      environment.apiUrl + `api/locations/${location.id}/`,
    );
  }
}
